import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAuth } from '../../contexts/AuthContext';
import { showGuestAlert } from '../../utils/guestReadOnlyAlert';
import { ensureChat } from '../../services/chatService';
import { listUsersExcept } from '../../services/userService';
import type { UserProfile } from '../../types/models';
import type { MainStackParamList } from '../../navigation/types';

type Props = NativeStackScreenProps<MainStackParamList, 'NewChat'>;

function displayLabel(p: UserProfile): string {
  const n = p.displayName?.trim();
  return n || `Annedaş ${p.id.slice(0, 6)}…`;
}

export function NewChatScreen({ navigation }: Props) {
  const insets = useSafeAreaInsets();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!user?.uid) {
        setUsers([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const list = await listUsersExcept(user.uid);
        setUsers(list);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user?.uid],
  );

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  async function onSelectPeer(peer: UserProfile) {
    if (user?.isAnonymous === true) {
      showGuestAlert(logout);
      return;
    }
    if (!user?.uid || openingId) {
      return;
    }
    setOpeningId(peer.id);
    try {
      const { chatId } = await ensureChat(user.uid, peer.id);
      navigation.replace('Chat', {
        chatId,
        otherUserId: peer.id,
        peerName: displayLabel(peer),
      });
    } finally {
      setOpeningId(null);
    }
  }

  if (loading && users.length === 0) {
    return (
 <View className="flex-1 items-center justify-center bg-white dark:bg-black">
        <ActivityIndicator size="large" color="#18181b" />
        <Text className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">Kişiler yükleniyor…</Text>
      </View>
    );
  }

  return (
 <View className="flex-1 bg-white dark:bg-black">
      <FlatList
        data={users}
        keyExtractor={item => item.id}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 12,
          paddingBottom: 24 + insets.bottom,
          flexGrow: 1,
        }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              void load({ silent: true });
            }}
            tintColor="#18181b"
          />
        }
        ListEmptyComponent={
          <View className="mt-8 rounded-2xl border border-zinc-200 bg-white px-6 py-10 dark:border-zinc-800 dark:bg-zinc-900">
            <Text className="text-center text-base font-semibold text-zinc-900 dark:text-zinc-100">
              Henüz başka kullanıcı yok
            </Text>
            <Text className="mt-2 text-center text-sm leading-6 text-zinc-500 dark:text-zinc-400">
              Topluluk büyüdükçe burada listelenecekler.
            </Text>
          </View>
        }
        renderItem={({ item }) => {
          const city = item.city?.trim();
          const district = item.district?.trim();
          const loc =
            city && district ? `${city} · ${district}` : city || district || 'Konum belirtilmemiş';
          const busy = openingId === item.id;
          return (
            <TouchableOpacity
              activeOpacity={0.88}
              disabled={Boolean(openingId)}
              onPress={() => void onSelectPeer(item)}
              className="mb-3 rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
 <View className="flex-row items-center justify-between gap-3">
 <View className="min-w-0 flex-1">
 <Text className="text-base font-semibold text-gray-900" numberOfLines={1}>
                    {displayLabel(item)}
                  </Text>
 <Text className="mt-1 text-sm text-gray-500" numberOfLines={1}>
                    {loc}
                  </Text>
                </View>
                {busy ? (
                  <ActivityIndicator size="small" color="#18181b" />
                ) : (
 <View className="h-10 w-10 items-center justify-center rounded-full bg-zinc-100">
 <Text className="text-lg">💬</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        }}
      />
    </View>
  );
}
